import { useCallback } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useOrder } from '~/api/orders'
import { getSafePaymentActionUrl } from '~/utils/paymentUrls'
import styles from './invoice.module.css'

/**
 * Format amount in IDR currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format date for invoice
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Get payment status label
 */
function getPaymentLabel(status: string | undefined): string {
  switch (status) {
    case 'PAID': return 'PAID'
    case 'PENDING': return 'AWAITING PAYMENT'
    case 'EXPIRED': return 'EXPIRED'
    case 'FAILED': return 'FAILED'
    case 'REFUNDED': return 'REFUNDED'
    default: return 'UNKNOWN'
  }
}

function formatPaymentMethod(method: string | null | undefined): string {
  if (!method) return '—'
  return method.replace(/_/g, ' ')
}

function formatPaymentProvider(provider: string | null | undefined): string {
  if (!provider) return '—'
  return provider.replace(/_/g, ' ')
}

/**
 * Printable Invoice Page
 * Designed for printing - minimal UI, clean layout
 */
export function InvoicePage() {
  const navigate = useNavigate()
  const { orderId } = useParams({ strict: false }) as { orderId: string }
  const { data: order, isLoading, isError } = useOrder(orderId)

  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  const handleBack = useCallback(() => {
    navigate({ to: '/admin/orders/$orderId', params: { orderId } })
  }, [navigate, orderId])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <span>Loading invoice...</span>
      </div>
    )
  }

  if (isError || !order) {
    return (
      <div className={styles.error}>
        <span>Invoice not found</span>
        <button onClick={handleBack}>← Back</button>
      </div>
    )
  }

  const isPaid = order.paymentStatus === 'PAID'
  const total = order.totalAmount + order.deliveryFee
  const paymentActionUrl = getSafePaymentActionUrl(order.paymentUrl)

  return (
    <div className={styles.invoicePage}>
      {/* Print Controls - hidden when printing */}
      <div className={styles.controls}>
        <button className={styles.backButton} onClick={handleBack}>
          ← BACK TO ORDER
        </button>
        <button className={styles.printButton} onClick={handlePrint}>
          🖨 PRINT INVOICE
        </button>
        {paymentActionUrl && (
          <a
            href={paymentActionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.providerActionLink}
          >
            OPEN PAYMENT ACTION →
          </a>
        )}
      </div>

      {/* Invoice Document */}
      <div className={styles.invoice}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.brand}>
            <h1 className={styles.brandName}>TASAMAGO</h1>
            <p className={styles.brandTagline}>Premium Fresh Eggs</p>
          </div>
          <div className={styles.invoiceMeta}>
            <h2 className={styles.invoiceTitle}>INVOICE</h2>
            <div className={styles.invoiceNumber}>#{order.externalId}</div>
            <div className={styles.invoiceDate}>{formatDate(order.createdAt)}</div>
          </div>
        </header>

        {/* Status Banner */}
        <div className={`${styles.statusBanner} ${isPaid ? styles.statusPaid : styles.statusUnpaid}`}>
          {getPaymentLabel(order.paymentStatus ?? undefined)}
        </div>

        <div className={styles.paymentMetaGrid}>
          <div>
            <span>Provider</span>
            <strong>{formatPaymentProvider(order.paymentProvider)}</strong>
          </div>
          <div>
            <span>Method</span>
            <strong>{formatPaymentMethod(order.paymentMethod)}</strong>
          </div>
          <div>
            <span>Channel</span>
            <strong>{order.paymentChannel ?? '—'}</strong>
          </div>
          <div>
            <span>Reference</span>
            <strong>{order.paymentProviderReference ?? '—'}</strong>
          </div>
          <div>
            <span>Transaction</span>
            <strong>{order.paymentTransactionId ?? '—'}</strong>
          </div>
        </div>

        {/* Addresses */}
        <div className={styles.addresses}>
          <div className={styles.addressBlock}>
            <h3 className={styles.addressLabel}>FROM</h3>
            <p className={styles.addressText}>
              <strong>Tasamago</strong><br />
              Jl. Telur Segar No. 1<br />
              Jakarta, Indonesia<br />
              hello@tasamago.com
            </p>
          </div>
          <div className={styles.addressBlock}>
            <h3 className={styles.addressLabel}>SHIP TO</h3>
            <p className={styles.addressText}>
              <strong>Customer #{order.customerId.slice(0, 8)}</strong><br />
              {order.deliveryAddress}
              {order.notes && (
                <>
                  <br />
                  <em>{order.notes}</em>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Line Items */}
        <table className={styles.itemsTable}>
          <thead>
            <tr>
              <th className={styles.thDescription}>Description</th>
              <th className={styles.thQty}>Qty</th>
              <th className={styles.thPrice}>Unit Price</th>
              <th className={styles.thAmount}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.tdDescription}>
                Fresh Eggs Order
                <span className={styles.itemNote}>Order #{order.externalId}</span>
              </td>
              <td className={styles.tdQty}>1</td>
              <td className={styles.tdPrice}>{formatCurrency(order.totalAmount)}</td>
              <td className={styles.tdAmount}>{formatCurrency(order.totalAmount)}</td>
            </tr>
            {order.deliveryFee > 0 && (
              <tr>
                <td className={styles.tdDescription}>Delivery Fee</td>
                <td className={styles.tdQty}>1</td>
                <td className={styles.tdPrice}>{formatCurrency(order.deliveryFee)}</td>
                <td className={styles.tdAmount}>{formatCurrency(order.deliveryFee)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div className={styles.totals}>
          <div className={styles.totalsRow}>
            <span>Subtotal</span>
            <span>{formatCurrency(order.totalAmount)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Delivery</span>
            <span>{formatCurrency(order.deliveryFee)}</span>
          </div>
          <div className={styles.totalsTotal}>
            <span>TOTAL</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.footer}>
          <div className={styles.footerNote}>
            <p>Thank you for your order!</p>
            <p className={styles.footerSmall}>
              For questions, contact hello@tasamago.com
            </p>
          </div>
          <div className={styles.footerMeta}>
            <p>Invoice generated on {formatDate(new Date().toISOString())}</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
